import {
  DocumentReference,
  FirestoreDataConverter,
  Timestamp,
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { getCurrentUser } from "@/services/auth";
import {
  TConfiguracaoFormularioIdeias,
  TConfiguracaoFormularioIdeiasDocument,
  CAMPOS_CONFIGURAVEIS_IDEIA
} from "./types";
import { getEmpresaDocumentReference } from "../empresas/firestore";
import ideiaSchema from "../ideias/schema/ideia-schema";

export const CONFIGURACAO_FORMULARIO_IDEIAS_COLLECTION_KEY = "configuracoesFormularioIdeias";
export const CONFIGURACAO_DOCUMENT_ID = "campos";

type TConfiguracaoFormularioIdeiasFirestoreDocument = TConfiguracaoFormularioIdeias & {
  updatedAt?: Timestamp;
  updatedBy?: string;
};

const configuracaoFormularioIdeiasConverter: FirestoreDataConverter<TConfiguracaoFormularioIdeiasDocument> = {
  toFirestore(data: TConfiguracaoFormularioIdeiasDocument): TConfiguracaoFormularioIdeiasFirestoreDocument {
    const { updatedAt, ...rest } = data;
    return {
      ...rest,
      updatedAt: serverTimestamp() as Timestamp,
      updatedBy: getCurrentUser()?.uid
    };
  },
  fromFirestore(snap): TConfiguracaoFormularioIdeiasDocument {
    const data = snap.data() as TConfiguracaoFormularioIdeiasFirestoreDocument;

    return {
      ...data,
      updatedAt: data.updatedAt?.toDate()
    };
  }
};

/**
 * Get configuracao formulario ideias collection reference from empresaId
 * @param empresaId - Empresa id to get the configuracao collection reference
 * @returns - Configuracao collection reference
 */
function getConfiguracaoFormularioIdeiasCollectionReference(empresaId: TEmpresa["id"]) {
  const empresaDocumentReference = getEmpresaDocumentReference(empresaId);
  return collection(empresaDocumentReference, CONFIGURACAO_FORMULARIO_IDEIAS_COLLECTION_KEY);
}

/**
 * Get configuracao formulario ideias document reference
 * @param empresaId - Empresa id to get the configuracao document reference
 * @returns - Configuracao document reference
 */
export function getConfiguracaoFormularioIdeiasDocumentReference(
  empresaId: TEmpresa["id"]
): DocumentReference<TConfiguracaoFormularioIdeiasDocument> {
  const configuracaoCollectionReference = getConfiguracaoFormularioIdeiasCollectionReference(empresaId);
  return doc(configuracaoCollectionReference, CONFIGURACAO_DOCUMENT_ID).withConverter(
    configuracaoFormularioIdeiasConverter
  );
}

/**
 * Get configuracao formulario ideias from Firestore
 * @param empresaId - Empresa id
 * @returns Configuracao ou null se não existir
 */
export async function getConfiguracaoFormularioIdeias(
  empresaId: TEmpresa["id"]
): Promise<TConfiguracaoFormularioIdeiasDocument | null> {
  const documentReference = getConfiguracaoFormularioIdeiasDocumentReference(empresaId);
  const documentSnap = await getDoc(documentReference);

  if (documentSnap.exists()) {
    return documentSnap.data();
  }

  return null;
}

/**
 * Save configuracao formulario ideias to Firestore
 * @param empresaId - Empresa id
 * @param configuracao - Configuracao to save
 */
export async function saveConfiguracaoFormularioIdeias(
  empresaId: TEmpresa["id"],
  configuracao: TConfiguracaoFormularioIdeias
): Promise<void> {
  const documentReference = getConfiguracaoFormularioIdeiasDocumentReference(empresaId);
  await setDoc(documentReference, {
    ...configuracao,
    updatedAt: new Date(),
    updatedBy: getCurrentUser()?.uid
  });
}

/**
 * Get default configuracao (todos os campos visíveis com títulos padrão do schema)
 * @returns Configuração padrão
 */
export function getDefaultConfiguracaoFormularioIdeias(): TConfiguracaoFormularioIdeias {
  const configuracaoPadrao = {} as TConfiguracaoFormularioIdeias;
  const schemaDescription = ideiaSchema.describe();

  for (const campo of CAMPOS_CONFIGURAVEIS_IDEIA) {
    const fieldDescription = schemaDescription.fields[campo];
    const defaultTitle = (fieldDescription as any)?.label || campo;

    configuracaoPadrao[campo] = {
      visivel: true,
      titulo: defaultTitle
    };
  }

  configuracaoPadrao.objetivos = [];

  return configuracaoPadrao;
}
